import type { SolMetadata } from '@seek/sol-js';
import { useTranslations } from '@vocab/react';
import {
  Box,
  Text,
  IconCompany,
  IconPeople,
  Stack,
  ButtonLink,
  IconArrow,
  Inline,
} from 'braid-design-system';
import striptags from 'striptags';

import translations from './.vocab';

import * as styles from './CompanyEVP.css';

const CompanyLogo = ({ src, inline }: { src: string; inline?: boolean }) => {
  const props = inline
    ? {
        borderRadius: 'standard' as const,
        boxShadow: 'borderNeutralLight' as const,
        position: 'absolute' as const,
        background: 'surface' as const,
      }
    : {};

  return (
    <Box
      component="img"
      src={src}
      padding="small"
      className={[
        styles.logoMaxWidth,
        {
          [styles.inlineLogo]: inline,
        },
      ]}
      {...props}
    />
  );
};

const CompanyEVPBranding = ({
  cover,
  logo,
}: Pick<CompanyEVPProps, 'logo' | 'cover'>) =>
  cover && (
    <Box
      display="flex"
      position="relative"
      data={{ automation: 'company-evp-cover-container' }}
    >
      <Box borderRadius="standard" component="img" src={cover} width="full" />
      <CompanyLogo src={logo} inline={true} />
    </Box>
  );

export type CompanyEVPProps = {
  id: string;
  logo: string;
  name: string;
  summary: string;
  url: string;
  cover?: string;
  industry?: string;
  size?: string;
  solMetadata?: SolMetadata;
};

export const CompanyEVP = ({
  cover,
  industry,
  logo,
  name,
  size,
  summary,
  url,
}: CompanyEVPProps) => {
  const { t } = useTranslations(translations);
  const hasCover = Boolean(cover);
  const summaryMaxLines = hasCover ? 2 : 3;

  return (
    <Box
      display="flex"
      flexDirection={{ mobile: 'columnReverse', tablet: 'row' }}
      gap={{ mobile: 'xlarge', tablet: 'medium' }}
      padding="medium"
    >
      <Box width="full">
        <Stack space="small">
          {hasCover ? (
            <Text size="large" weight="medium">
              {name}
            </Text>
          ) : (
            <Inline space="small" alignY="center" collapseBelow="tablet">
              <CompanyLogo src={logo} />
              <Text size="large" weight="medium">
                {name}
              </Text>
            </Inline>
          )}

          {size || industry ? (
            <Box
              display="flex"
              gap="small"
              flexDirection={{
                mobile: 'column',
                tablet: hasCover ? 'column' : 'row',
              }}
            >
              {industry && (
                <Text icon={<IconCompany />} size="small" tone="secondary">
                  {industry}
                </Text>
              )}

              {size && (
                <Text icon={<IconPeople />} size="small" tone="secondary">
                  {size}
                </Text>
              )}
            </Box>
          ) : null}

          <Text size="small" maxLines={summaryMaxLines}>
            {striptags(summary)}
          </Text>

          <Inline space="none">
            <ButtonLink
              href={url}
              target="_blank"
              variant="soft"
              iconPosition="trailing"
              icon={<IconArrow direction="right" />}
            >
              {t('View company')} &nbsp;
            </ButtonLink>
          </Inline>
        </Stack>
      </Box>

      {hasCover && (
        <Box width="full">
          <CompanyEVPBranding cover={cover} logo={logo} />
        </Box>
      )}
    </Box>
  );
};
